import React,{ useCallback, useEffect, useRef, useState } from 'react';
import { getFeedsKeyword } from '../../services/feeds'; 
import LoadingCommon from '../../components/LoadingCommon';
import KeywordItem from '../../components/Snippets/KeywordItem';
import { keywordLanguageMap } from '../../utils/languageValue';
import { LanguageType } from '../../types/common';
import NoData from '../../components/NoDataCommon';
import { NO_DATE } from '../../constants';
import { useGetSingePageSourceRule } from '../useGetPageSourceRule';
// import cryptoJs from 'crypto-js';

export default function useKeywordColumn({
  dealwithParams,
  dealwithKey,
  concurrencyNumber = 3,
  pageIndex,
  pageSize,
  commonParams,
  getKeywordFunc = getFeedsKeyword,
  dealwithResponse = (data: any, filter: any) => data,
}: {
  dealwithParams: (item: any, commonParams: any) => {};
  dealwithKey: (param: any) => string;
  concurrencyNumber?: number;
  pageIndex?: number;
  pageSize?: number;
  commonParams: any;
  getKeywordFunc?: (
    value: any,
    cancelTokenFunc?: (cancel: any) => void,
  ) => any;
  dealwithResponse?: (data: any, filter: any) => any;
}) {
  const [keywordMap, setKeywordMap] = useState<any>({});
  const localKeywordMap: any = useRef({});
  const [record, setRecord] = useState<Array<any>>([]);
  // 组件是否仍挂载：卸载后不再推进请求队列 / setState
  const mountedRef = useRef(true);
  // 收集所有在途请求的 cancel 函数，卸载时统一取消
  const cancelersRef = useRef<Array<(reason?: any) => void>>([]);
  const getKeywordCacheKey = (id: string) => {
    const { start_time, end_time } = commonParams;
    // 使用CryptoJS的MD5函数进行加密
    // const hashed = cryptoJs.MD5(JSON.stringify(commonParams));
    // id === 'nezha' &&
    //   console.log(id, '*****----------', hashed.toString(cryptoJs.enc.Hex));
    return start_time + end_time + id;
  };
  // 组件卸载：取消所有在途请求并标记已卸载，避免离开页面后接口继续请求 / 卸载后 setState
  useEffect(() => {
    // setup 时重置标记，兼容 React StrictMode 下 setup→cleanup→setup 的双重调用
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelersRef.current.forEach(cancel => cancel());
      cancelersRef.current = [];
    };
  }, []);
  useEffect(() => {
    let currentGameList = record || [];
    if (pageSize && pageIndex) {
      const startPage = pageIndex - 1;
      currentGameList = record.slice(
        startPage * pageSize,
        pageIndex * pageSize,
      );
    }
    requestKeyword(currentGameList);
  }, [pageSize, pageIndex, record]);

  // 手动处理并发
  const requestKeyword = useCallback(
    (requestList: Array<any>) => {
      if (requestList.length === 0) {
        return;
      }
      let i = 0;

      // 请求结束后从在途列表中移除其 cancel 函数，避免数组无限增长
      const removeCanceler = (cancel: any) => {
        if (!cancel) return;
        cancelersRef.current = cancelersRef.current.filter(c => c !== cancel);
      };

      // 参数列表
      const params = requestList
        .map((item: any) => {
          const local = dealwithParams(item, commonParams);
          const key = getKeywordCacheKey(dealwithKey(local));
          if (localKeywordMap.current[key]) {
            return null;
          }
          return dealwithParams(item, commonParams);
        })
        .filter(Boolean);

      const setDataWithRequest = (param: any, data = []) => {
        // 组件已卸载：既不推进并发队列，也不 setState
        if (!mountedRef.current) return;

        const key = getKeywordCacheKey(dealwithKey(param));
        localKeywordMap.current = {
          ...localKeywordMap.current,
          [key]: { data, loading: false },
        };

        const newParam = params[i];
        if (newParam) {
          getKeywordList(newParam);
          i++;
        }
        // 1 ， 1/2  1/3  1/4 刷新一次
        if ([1, 2, 3, 4].includes(params.length / i)) {
          setKeywordMap(localKeywordMap.current);
        }
        // setKeywordMap(localKeywordMap.current);
      };

      // 请求方法
      const getKeywordList = (param: any) => {
        let currentCancel: any = null;
        const cancelTokenFunc = (cancel: any) => {
          currentCancel = cancel;
          cancelersRef.current.push(cancel);
        };
        getKeywordFunc(param, cancelTokenFunc)
          .then((data: any) => {
            removeCanceler(currentCancel);
            setDataWithRequest(param, dealwithResponse(data, params));
          })
          .catch(() => {
            removeCanceler(currentCancel);
            setDataWithRequest(param);
          });
      };

      // 并发数量
      for (let index = 0; index < concurrencyNumber; index++) {
        const newParam = params[index];
        if (newParam) {
          getKeywordList(newParam);
          i++;
        }
      }
    },
    [commonParams],
  );

  function KeywordColumn({
    dataKey,
    hideCount = false,
    showAll = false,
    active = true,
    itemClick = () => {},
    language = 'origin',
    filterKeyword = (value: Array<any>) => value,
  }: {
    dataKey: string;
    hideCount?: boolean;
    showAll?: boolean;
    active?: boolean;
    itemClick?: (keywordList: Array<string>) => void;
    language?: LanguageType;
    filterKeyword?: (value: Array<any>) => Array<any>;
  }) {
    const key = getKeywordCacheKey(dataKey);
    let { loading, data } = keywordMap[key] || { loading: true };

    let styleObj: any = { display: 'flex', flexWrap: 'wrap' };
    // 换行，就去掉height，并加上marginBottom
    if (!showAll) {
      styleObj = {
        ...styleObj,
        overflow: 'hidden',
        height: 22,
        // marginBottom: -10,
      };
    }

    const { Keyword_Color } = useGetSingePageSourceRule({
      keys: ['Keyword_Color'],
    });

    data = filterKeyword(data);

    const renderContent = () => {
      if (loading) {
        return <LoadingCommon status="loading" />;
      }

      if (!data || data.length === 0) {
        return (
          <NoData
            style={{ height: 16, fontSize: 12, margin: 0 }}
            hasTitle
            hasDesc={false}
            hasImg={false}
            emptyTitleText={NO_DATE.DEFAULT}
          />
        );
      }

      return data.map((item: any) => (
        <KeywordItem
          key={item.keyword}
          sentiment_rating={Keyword_Color ? 3 : item.sentiment_rating}
          count={item.count}
          keyword={item}
          hideCount={hideCount}
          active={active}
          itemClick={() =>
            itemClick(item.child.map((child: any) => child.keyword))
          }
          languageMap={keywordLanguageMap}
          language={language}
        />
      ));
    };

    return (
      <div className="table-flex" style={styleObj}>
        {renderContent()}
      </div>
    );
  }

  return { setRecord, KeywordColumn, keywordMap };
}
